import assert from "assert"
import {
  CommonErrorsKey,
  contractBook,
  isSupportedChainId,
} from "@eulerxyz/core-sdk"
import {
  type Account,
  type Address,
  type Chain,
  type Client,
  type Transport,
  decodeErrorResult,
  decodeFunctionResult,
  getAddress,
  publicActions,
  zeroAddress,
} from "viem"
import {
  BaseAggregator,
  type PendingResolve as BasePendingResolve,
} from "../BaseAggregator"
import {
  type BatchResult,
  type BatchResultStatus,
  type ContractFunctionResult,
  type EVCCalls,
  type FailureBatchResult,
  type SuccessBatchResult,
  mapToEVCCall,
} from "../evc"
import { getPythPriceUpdate } from "./utils"

type Result = ContractFunctionResult<
  typeof contractBook.accountLens.abi,
  "getAccountInfo"
>

type PendingResolve = BasePendingResolve<BatchResult<Result>>

/**
 * AccountAggregator class for managing accounts and performing batched contract calls.
 * @template account
 * @extends BaseAggregator<SuccessBatchResult<Result> | FailureBatchResult, Transport, Chain, account>
 */
export class AccountAggregator<
  account extends Account | undefined,
> extends BaseAggregator<
  SuccessBatchResult<Result> | FailureBatchResult,
  Transport,
  Chain,
  account
> {
  #evcAddress: Address
  #accountLensAddress: Address
  #pendingAccounts: [Address, Address][] = []

  /**
   * Creates an instance of AccountAggregator.
   * @param {Client<Transport, Chain, account>} client - The client to use for batched contract calls.
   */
  constructor(client: Client<Transport, Chain, account>) {
    super(client)
    assert(isSupportedChainId(client.chain.id), "UNSUPPORTED_CHAIN_ID")
    this.#evcAddress = contractBook.evc.address[client.chain.id]
    this.#accountLensAddress = contractBook.accountLens.address[client.chain.id]
  }

  /**
   * Gets information about a specific account.
   * @param {Address} account - The address of the account.
   * @param {Address} vault - The address of the vault.
   * @returns {Promise<BatchResultStatus<Result>>} - A promise that resolves with the account information.
   */
  getAccount(
    account: Address,
    vault: Address,
  ): Promise<BatchResultStatus<Result>> {
    this.scheduleBatch()

    this.#pendingAccounts.push([getAddress(account), getAddress(vault)])

    // Return a promise that will be resolved or rejected when the batch is executed.
    return new Promise<BatchResult<Result>>((resolve, reject) => {
      this.pendingResolves.push(resolve)
      this.pendingRejects.push(reject)
    })
  }

  /**
   * Makes a batch request to get vault information.
   * @protected
   * @override
   */
  protected override async makeBatchNow() {
    const pendingResolves = this.pendingResolves
    const pendingRejects = this.pendingRejects
    const pendingAccounts = this.#pendingAccounts
    this.pendingResolves = []
    this.pendingRejects = []
    this.#pendingAccounts = []

    return this.#getAccounts(pendingAccounts, pendingResolves, pendingRejects)
  }

  /**
   * Fetches information about multiple accounts.
   * @param {[Address, Address][]} accounts - The args for account info call.
   * @param {PendingResolve[]} pendingResolves - The pending resolves for each account.
   * @param {((error: Error) => void)[]} pendingRejects - The pending rejects for each account.
   */
  async #getAccounts(
    accounts: [Address, Address][],
    pendingResolves: PendingResolve[],
    pendingRejects: ((error: Error) => void)[],
  ) {
    try {
      const oracles = await getPythPriceUpdate(this.chainId, this.client)
      const accountInfoBatchItems = mapToEVCCall(
        accounts.map((args) => ({
          address: this.#accountLensAddress,
          abi: contractBook.accountLens.abi,
          onBehalfOfAccount: zeroAddress,
          functionName: "getAccountInfo",
          args: args,
          value: 0n,
        })),
      )

      const BATCH_SIZE = 100
      for (let i = 0; i < accountInfoBatchItems.length; i += BATCH_SIZE) {
        const chunk = accountInfoBatchItems.slice(i, i + BATCH_SIZE)
        const chunkAccounts = accounts.slice(i, i + BATCH_SIZE)
        const chunkResolves = pendingResolves.slice(i, i + BATCH_SIZE)
        const chunkRejects = pendingRejects.slice(i, i + BATCH_SIZE)

        const { result } = await this.#batch(
          [...oracles, ...chunk],
          oracles.reduce((acc, cur) => acc + cur.value, 0n),
        )
        const [batchItemResults] = result

        for (const [j, batch] of batchItemResults
          .slice(oracles.length)
          .entries()) {
          const pendingResolve = chunkResolves[j]
          const pendingReject = chunkRejects[j]
          const [account, vault] = chunkAccounts[j] || []

          if (!pendingResolve || !pendingReject || !vault || !account) {
            throw new Error("Invalid object length")
          }

          if (batch.result === "0x") {
            console.error("[#getAccounts]batch.result", batch.result)
            pendingResolve?.({
              status: "failure",
              error: { errorName: CommonErrorsKey.UnknownError },
              data: batch.result,
            })
            continue
          }

          if (batch.success) {
            const result = decodeFunctionResult({
              abi: contractBook.accountLens.abi,
              data: batch.result,
              functionName: "getAccountInfo",
            })
            pendingResolve({ status: "success", result })
          } else {
            const errorResult = decodeErrorResult({
              abi: contractBook.vaultLens.abi,
              data: batch.result,
            })
            pendingResolve({
              status: "failure",
              error: { errorName: errorResult.errorName },
              data: batch.result,
            })
          }
        }
      }
    } catch (error) {
      for (const pendingReject of pendingRejects) {
        pendingReject(error as Error)
      }
    }
  }

  /**
   * Simulates a batch of contract calls.
   * @param {EVCCalls} calls - The calls to include in the batch.
   * @param {bigint} [value=0n] - The value to pass with the call.
   */
  #batch(calls: EVCCalls, value = 0n) {
    return this.client.extend(publicActions).simulateContract({
      address: this.#evcAddress,
      abi: contractBook.evc.abi,
      functionName: "batchSimulation",
      args: [calls],
      value: value,
      stateOverride: [{ address: zeroAddress, balance: value * 4000n }],
    })
  }
}
